import * as Slider from '@radix-ui/react-slider'
import RangeHandleLeft from 'assets/range-handle.svg?react'
import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Color } from 'three'
import { GeckoMeasurementRange, GeckoMeasurementUnit } from 'types'

const SliderRoot = styled(Slider.Root)`
  position: relative;
  display: flex;
  align-items: center;
  padding: 5px 0;
`

const SliderTrack = styled(Slider.Track)`
  position: relative;
  border-radius: 30px;
  overflow: hidden;
  height: 24px;
`

const GraySliderOverlay = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(170, 170, 170, 1);
`

const SliderThumb = styled(Slider.Thumb)`
  display: block;
  width: max-content;
  padding-top: 1px;

  &:focus {
    outline: none;
  }
`

const RangeHandleRight = styled(RangeHandleLeft)`
  transform: rotate(180deg);
`

function generateHsvGradient(
  canvas: HTMLCanvasElement,
  width: number,
  height: number
) {
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to get 2D context')
  }

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const hue = x / width
      const saturation = 1
      const value = 0.5

      const rgb = new Color().setHSL(hue, saturation, value)
      ctx.fillStyle = `#${rgb.getHexString()}`
      ctx.fillRect(x, y, 1, 1)
    }
  }
}

export const HSVFilterSlider: React.FC<{
  measurementUnit: GeckoMeasurementUnit
  step: number
  fullValueRange: GeckoMeasurementRange
  filteredValueRange: GeckoMeasurementRange
  setFilteredValueRange: (range: GeckoMeasurementRange) => void
}> = ({
  measurementUnit,
  step,
  fullValueRange,
  filteredValueRange,
  setFilteredValueRange,
}) => {
  //----------------------------------------------------------------------------

  const onValueFilterChanged = React.useCallback<
    ([min, max]: [number, number]) => void
  >(
    ([min, max]) => {
      setFilteredValueRange(
        GeckoMeasurementRange.CreateFromValues(min, max, measurementUnit)
      )
    },
    [setFilteredValueRange, measurementUnit]
  )

  //----------------------------------------------------------------------------

  useEffect(() => {
    const canvas = document.getElementById('hsvCanvas') as HTMLCanvasElement
    generateHsvGradient(canvas, 1000, 1)
  }, [])

  const minSideWidth = filteredValueRange.min.num - fullValueRange.min.num
  const maxSideWidth = fullValueRange.max.num - filteredValueRange.max.num
  const totalWidth = fullValueRange.max.num - fullValueRange.min.num

  return (
    <SliderRoot
      min={fullValueRange.min.num}
      max={fullValueRange.max.num}
      value={[
        Math.max(filteredValueRange.min.num, fullValueRange.min.num),
        Math.min(filteredValueRange.max.num, fullValueRange.max.num),
      ]}
      step={step}
      onValueChange={onValueFilterChanged}>
      <SliderTrack>
        <GraySliderOverlay
          style={{ left: 0, width: `${(minSideWidth / totalWidth) * 100}%` }}
        />
        <canvas style={{ width: '100%', height: '100%' }} id='hsvCanvas' />
        <GraySliderOverlay
          style={{ right: 0, width: `${(maxSideWidth / totalWidth) * 100}%` }}
        />
      </SliderTrack>
      <SliderThumb style={{ cursor: 'e-resize' }}>
        <RangeHandleLeft style={{ cursor: 'e-resize' }} />
      </SliderThumb>
      <SliderThumb style={{ cursor: 'w-resize' }}>
        <RangeHandleRight style={{ cursor: 'w-resize' }} />
      </SliderThumb>
    </SliderRoot>
  )
}
